import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, map, debounceTime } from 'rxjs';
import { CoordinatorService } from './coordinator.service';

/**
 * Counter types available in the system
 */
export type COUNTER_TYPE =
    | 'total'
    | 'active'
    | 'seen'
    | 'rejected'
    | 'bookmarked'
    | 'main'
    | 'preview'
    | 'previewSize'
    | 'collectionSize';

/**
 * State interface for all counter values
 */
export interface CounterState {
    /** Timestamp of last calculation */
    timestamp: number;
    /** Whether calculation is in progress */
    isCalculating: boolean;
    /** Counter values */
    values: {
        /** Total items in current dataset (preview or collection) */
        total: number;
        /** Items after filters applied */
        active: number;
        /** Items marked as seen (preview mode) */
        seen: number;
        /** Items marked as rejected (preview mode) */
        rejected: number;
        /** Items marked as bookmarked (collection mode) */
        bookmarked: number;
        /** Items in active list that exist in collection (when in preview) */
        main: number;
        /** Items in active list that exist in preview (when in collection) */
        preview: number;
        /** Total size of preview dataset */
        previewSize: number;
        /** Total size of collection dataset */
        collectionSize: number;
    };
}

/**
 * Initial state with all counters at zero
 */
const INITIAL_STATE: CounterState = {
    timestamp: 0,
    isCalculating: false,
    values: {
        total: 0,
        active: 0,
        seen: 0,
        rejected: 0,
        bookmarked: 0,
        main: 0,
        preview: 0,
        previewSize: 0,
        collectionSize: 0,
    },
};

/**
 * Centralized counter service for managing all count displays in the application.
 * 
 * This service provides a single source of truth for all counter values,
 * ensuring consistency and preventing race conditions between different
 * components displaying the same counts.
 * 
 * Usage:
 * - Subscribe to specific counter streams (e.g., `previewSize$`, `active$`)
 * - Call `requestRecalculation()` when data changes
 * - Use `get()` for synchronous access (use with caution)
 */
export class Counter$ {
    private _state$ = new BehaviorSubject<CounterState>(INITIAL_STATE);
    private _recalculateRequest$ = new Subject<void>();

    /** Full state observable - use specific counter streams when possible */
    state$ = this._state$.asObservable();

    /** Observable indicating calculation in progress */
    isCalculating$ = this.state$.pipe(
        map((s) => s.isCalculating),
        distinctUntilChanged()
    );

    // Individual counter streams with distinctUntilChanged for performance
    
    /** Preview dataset size */
    previewSize$ = this.state$.pipe(
        map((s) => s.values.previewSize),
        distinctUntilChanged()
    );

    /** Collection dataset size */
    collectionSize$ = this.state$.pipe(
        map((s) => s.values.collectionSize),
        distinctUntilChanged()
    );

    /** Total items in current dataset */
    total$ = this.state$.pipe(
        map((s) => s.values.total),
        distinctUntilChanged()
    );

    /** Active (filtered) items count */
    active$ = this.state$.pipe(
        map((s) => s.values.active),
        distinctUntilChanged()
    );

    /** Seen items count */
    seen$ = this.state$.pipe(
        map((s) => s.values.seen),
        distinctUntilChanged()
    );

    /** Rejected items count */
    rejected$ = this.state$.pipe(
        map((s) => s.values.rejected),
        distinctUntilChanged()
    );

    /** Bookmarked items count */
    bookmarked$ = this.state$.pipe(
        map((s) => s.values.bookmarked),
        distinctUntilChanged()
    );

    /** Items in collection (from preview view) */
    main$ = this.state$.pipe(
        map((s) => s.values.main),
        distinctUntilChanged()
    );

    /** Items in preview (from collection view) */
    preview$ = this.state$.pipe(
        map((s) => s.values.preview),
        distinctUntilChanged()
    );

    constructor(private coords: CoordinatorService) {
        // Debounce recalculation requests to avoid excessive computation
        this._recalculateRequest$
            .pipe(debounceTime(50))
            .subscribe(() => this._recalculate());
    }

    /**
     * Request a recalculation of all counters.
     * Debounced to prevent excessive computation during rapid changes.
     */
    requestRecalculation(): void {
        this._recalculateRequest$.next();
    }

    /**
     * Get a specific counter value synchronously.
     * Prefer using the observable streams for reactive updates.
     * 
     * @param type - The counter type to retrieve
     * @returns The current counter value
     */
    get(type: COUNTER_TYPE): number {
        const state = this._state$.getValue();
        return state.values[type] ?? 0;
    }

    /**
     * Get the observable stream for a specific counter type.
     * 
     * @param type - The counter type
     * @returns Observable stream for that counter
     */
    select$(type: COUNTER_TYPE) {
        switch (type) {
            case 'total':
                return this.total$;
            case 'active':
                return this.active$;
            case 'seen':
                return this.seen$;
            case 'rejected':
                return this.rejected$;
            case 'bookmarked':
                return this.bookmarked$;
            case 'main':
                return this.main$;
            case 'preview':
                return this.preview$;
            case 'previewSize':
                return this.previewSize$;
            case 'collectionSize':
                return this.collectionSize$;
            default:
                return this.state$.pipe(map(() => 0));
        }
    }

    /**
     * Get the current full state synchronously.
     * Prefer using state$ observable for reactive updates.
     */
    get currentState(): CounterState {
        return this._state$.getValue();
    }

    /**
     * Internal recalculation method.
     * Calculates all counters atomically and updates state.
     */
    private _recalculate(): void {
        const current = this._state$.getValue();
        
        // Set calculating flag
        this._state$.next({ ...current, isCalculating: true });

        try {
            const values = this._calculateAllCounts();
            
            this._state$.next({
                timestamp: Date.now(),
                isCalculating: false,
                values,
            });
        } catch (error) {
            console.error('Counter$ calculation failed:', error);
            // Restore previous state on error
            this._state$.next({ ...current, isCalculating: false });
        }
    }

    /**
     * Calculate all counter values.
     * This method is synchronous but reads from the latest data sources.
     */
    private _calculateAllCounts(): CounterState['values'] {
        const dataset$ = this.coords.dataset$;
        const resultList$ = this.coords.resultList$;
        const view$ = this.coords.view$;

        // Dataset sizes
        const previewSize = this._safeSize(dataset$.preview);
        const collectionSize = this._safeSize(dataset$.main);

        // Current dataset total
        const total = this._safeSize(dataset$.current);

        // Active list from result list
        const activeList = resultList$.activeList$.getValue() || [];
        const active = activeList.length;

        // Active ID set for view counting
        const activeIdSet = resultList$.activeIdSet || new Set<string>();

        // View-based counters
        const seen = this._countInView('__seen', activeIdSet, view$);
        const rejected = this._countInView('__rejected', activeIdSet, view$);
        const bookmarked = this._countInView('__bookmarked', activeIdSet, view$);

        // Cross-dataset counters
        const main = this._countInOtherDataset(activeList, 'main', dataset$);
        const preview = this._countInOtherDataset(activeList, 'preview', dataset$);

        return {
            total,
            active,
            seen,
            rejected,
            bookmarked,
            main,
            preview,
            previewSize,
            collectionSize,
        };
    }

    /**
     * Safely get size from a dataset, handling undefined/null cases.
     */
    private _safeSize(dataset: any): number {
        if (!dataset) return 0;
        const size = dataset.size;
        return this._validateCount(size, 'dataset.size');
    }

    /**
     * Count items in a view that are also in the active set.
     */
    private _countInView(viewId: string, activeIdSet: Set<string>, view$: any): number {
        if (!view$) return 0;
        
        const view = view$.get(viewId);
        if (!view) return 0;

        const count = view.countInSet(activeIdSet);
        return this._validateCount(count, viewId);
    }

    /**
     * Count items in active list that exist in the other dataset.
     */
    private _countInOtherDataset(
        activeList: any[],
        targetDataset: 'main' | 'preview',
        dataset$: any
    ): number {
        if (!activeList || !dataset$) return 0;

        let count = 0;
        
        if (targetDataset === 'main') {
            // Count items that have a corresponding ID in main (collection)
            count = activeList.filter((x) => dataset$.fromPreview2MainId(x?.id)).length;
        } else {
            // Count items that have a corresponding ID in preview
            count = activeList.filter((x) => dataset$.fromMain2PreviewId(x?.id)).length;
        }

        return this._validateCount(count, `inOther_${targetDataset}`);
    }

    /**
     * Validate a count value, ensuring it's a valid non-negative integer.
     */
    private _validateCount(count: number, context: string): number {
        if (!Number.isFinite(count) || count < 0) {
            console.warn(`Counter$: Invalid count for ${context}: ${count}, defaulting to 0`);
            return 0;
        }
        return Math.floor(count);
    }

    /**
     * Reset all counters to zero.
     * Called when clearing project data.
     */
    reset(): void {
        this._state$.next(INITIAL_STATE);
    }
}

